export type ShellHeaderPolicyReason =
  | 'enabled_default'
  | 'enforced_parent_financial_aid_title_for_route_consistency_2026_02_19'
  | 'enforced_students_dashboard_shell_header_enabled_for_route_consistency_2026_02_28'
  | 'enforced_students_family_tabs_shell_header_enabled_for_route_consistency_2026_03_01'
  | 'enforced_students_routes_content_only_headerless_for_visual_parity_2026_02_28';

export type ShellHeaderPolicy = {
  showHeaderTitle: boolean;
  showHeaderActions: boolean;
  reason: ShellHeaderPolicyReason;
};

type ShellHeaderPolicyInput = {
  pathname: string;
  persona: string | null | undefined;
  defaults: {
    showHeaderTitle: boolean;
    showHeaderActions: boolean;
  };
};

const normalizePathname = (value: string): string => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '/';
  const withoutQuery = trimmed.split('?')[0] ?? trimmed;
  const normalized = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  return normalized.toLowerCase();
};

const normalizePersona = (value: string | null | undefined): string =>
  String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const pathnameMatchesPrefix = (pathname: string, prefix: string): boolean => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
);

const isParentFinancialAidPath = (pathname: string): boolean => (
  pathnameMatchesPrefix(pathname, '/parent/portal/financialaid')
  || pathnameMatchesPrefix(pathname, '/parent/portal--new/financialaid')
  || pathnameMatchesPrefix(pathname, '/portal/family/financial-aid')
  || pathnameMatchesPrefix(pathname, '/portal/family/financialaid')
);

const isStudentsFamiliesPath = (pathname: string): boolean => (
  pathnameMatchesPrefix(pathname, '/school/students')
);

const isStudentsFamiliesDashboardPath = (pathname: string): boolean => (
  pathname === '/school/students'
  || pathname === '/school/students/dashboard'
);

const isStudentsFamiliesFamilyTabsPath = (pathname: string): boolean => (
  pathnameMatchesPrefix(pathname, '/school/students/family')
);

export const getShellHeaderPolicy = (input: ShellHeaderPolicyInput): ShellHeaderPolicy => {
  const pathname = normalizePathname(input.pathname);
  const persona = normalizePersona(input.persona);

  if (persona === 'parent' && isParentFinancialAidPath(pathname)) {
    // Decision log: documentation/reports/YSTNJ-PARENT-FINANCIAL-AID-HEADER-TITLE-DECISION-2026-02-19.md
    // Parent active routes intentionally keep a visible header title for route consistency.
    return {
      showHeaderTitle: true,
      showHeaderActions: true,
      reason: 'enforced_parent_financial_aid_title_for_route_consistency_2026_02_19',
    };
  }

  if (isStudentsFamiliesPath(pathname)) {
    if (isStudentsFamiliesDashboardPath(pathname)) {
      return {
        showHeaderTitle: true,
        showHeaderActions: true,
        reason: 'enforced_students_dashboard_shell_header_enabled_for_route_consistency_2026_02_28',
      };
    }

    if (isStudentsFamiliesFamilyTabsPath(pathname)) {
      return {
        showHeaderTitle: true,
        showHeaderActions: true,
        reason: 'enforced_students_family_tabs_shell_header_enabled_for_route_consistency_2026_03_01',
      };
    }

    return {
      showHeaderTitle: false,
      showHeaderActions: false,
      reason: 'enforced_students_routes_content_only_headerless_for_visual_parity_2026_02_28',
    };
  }

  return {
    showHeaderTitle: input.defaults.showHeaderTitle,
    showHeaderActions: input.defaults.showHeaderActions,
    reason: 'enabled_default',
  };
};
